import { getLocaliceKeyFromErrorCode } from "../services/error-codes"
import HeapsError from "./heaps-error"
import { TFunction } from "i18next"
import { useCallback, useEffect, useRef, useSyncExternalStore } from "react"
import { useGlobalModals } from "../context/GlobalModalsContext"
import { errorCodes } from "../services/error-codes"
import {
  getCachedUser,
  SessionRefusedKind,
  subscribeCachedUser,
  subscribeSessionRefused,
} from "../services/cached-user"
import { useI18next } from "gatsby-plugin-react-i18next"
import { routes } from "../routes"
import { translationKeys } from "./translationKeys"
import { captureException } from "../services/exception-logger"

type Error = {
  code: string
  detail?: string
}

// this is format that comes from our backend
type ErrorResponse = {
  errors: Error[]
}

const fallBackErrorMessage = "An unexpected error occurred"

export const fallbackError = {
  errorMessage: fallBackErrorMessage,
  translationKey: "",
  errorCode: "unknown_error",
}

export const extractErrorMessageErrorCodeAndTranslationKey = (
  apiError: ErrorResponse | undefined
) => {
  if (!apiError || !apiError.errors || !apiError.errors.length) {
    return fallbackError
  }
  const errorCode = apiError.errors[0].code
  const errorDetail = apiError.errors[0].detail
  const localiceKey = getLocaliceKeyFromErrorCode(errorCode)
  // prio for error message: 1) detail, 2) translation key, 3) general fallback
  const errorMessage = errorDetail ? errorDetail : ""
  const translationKey = errorMessage ? "" : localiceKey ? localiceKey : ""
  return { errorMessage, translationKey, errorCode }
}

export const getErrorMessage = (error: unknown, t: TFunction) => {
  if (!(error instanceof HeapsError)) {
    // Everything our fetch layer throws is a HeapsError, so anything else
    // reaching user-facing error handling is a bug on our side
    captureException(error, {
      type: "business_logic",
      category: "invalid-state",
      context: { errorSource: "getErrorMessage - Not HeapsError" },
    })
    // Return a fallback message. This is not something we called ourselves
    return fallBackErrorMessage
  }

  if (error.errorMessage) {
    return error.errorMessage
  }
  if (error.translationKey) {
    return t(error.translationKey)
  }
  return fallBackErrorMessage
}

export const getErrorCode = (error: unknown) => {
  if (!(error instanceof HeapsError)) {
    captureException(error, {
      type: "business_logic",
      category: "invalid-state",
      context: { errorSource: "getErrorCode - Not HeapsError" },
    })
    // Return a fallback message. This is not something we called ourselves
    return ""
  }

  return error.errorCode
}

// Which session a refused, `on401: "throw"` request belonged to; undefined for
// every other error
export const getSessionExpiredKind = (
  error: unknown
): "user" | "guest" | undefined => {
  if (!(error instanceof HeapsError)) {
    return undefined
  }
  if (error.errorCode === errorCodes.SESSION_EXPIRED_USER) {
    return "user"
  }
  if (error.errorCode === errorCodes.SESSION_EXPIRED_GUEST) {
    return "guest"
  }
  return undefined
}

const getCachedAccessToken = () => getCachedUser()?.access_token ?? null
const getServerAccessToken = () => null

// A user's order survives a refused session - they sign in again and carry
// on, so the login modal opens and `onReauthenticated` fires once a new token
// lands. A guest's order died with the session; the page decides what to show.
//
// The fetch layer broadcasts every refusal, whichever call hit it (query,
// mutation, direct api call), so this does not need the error itself. A
// refused token is handled once however many calls it fails - until the
// login modal is closed without signing in.
export const useHandleSessionExpired = (handlers: {
  onGuestExpired: () => void
  onReauthenticated: () => void
}) => {
  const { visibleAuthModal, setVisibleAuthModal } = useGlobalModals()
  const token = useSyncExternalStore(
    subscribeCachedUser,
    getCachedAccessToken,
    getServerAccessToken
  )
  const handlersRef = useRef(handlers)
  handlersRef.current = handlers
  // The dead token stays cached while the modal is open, so "re-authenticated"
  // means the token differs from the one that was refused
  const refused = useRef<{ token: string | null; kind: SessionRefusedKind }>()

  const handleRefused = useCallback(
    (kind: SessionRefusedKind) => {
      const current = getCachedAccessToken()
      if (refused.current?.token === current) {
        return
      }
      refused.current = { token: current, kind }
      if (kind === "user") {
        setVisibleAuthModal("login")
      } else {
        handlersRef.current.onGuestExpired()
      }
    },
    [setVisibleAuthModal]
  )

  useEffect(() => subscribeSessionRefused(handleRefused), [handleRefused])

  useEffect(() => {
    if (refused.current?.kind === "user" && token !== refused.current.token) {
      refused.current = undefined
      handlersRef.current.onReauthenticated()
    }
  }, [token])

  // Closing the login modal without signing in leaves the dead token cached.
  // Forget it, so the next refused call opens the modal again instead of
  // failing silently
  useEffect(() => {
    if (
      visibleAuthModal === undefined &&
      refused.current?.kind === "user" &&
      refused.current.token === getCachedAccessToken()
    ) {
      refused.current = undefined
    }
  }, [visibleAuthModal])
}

export const useHandleFetchError = (
  error: unknown,
  t: TFunction,
  navigateTo404: boolean = true
) => {
  const { setInfoModalConfig } = useGlobalModals()
  const { navigate } = useI18next()

  return useEffect(() => {
    // Session expiry has its own handling (useHandleSessionExpired)
    if (!error || getSessionExpiredKind(error)) {
      return
    }
    const errorMessage = getErrorMessage(error, t)
    setInfoModalConfig({
      title: t(translationKeys.comboPaymentErrorGenericHeader),
      description: errorMessage,
      onOkayClick: () => {
        if (!navigateTo404) {
          return
        }
        navigate(routes.notFound)
      },
    })
  }, [error])
}
